import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Business,
  HealthAndSafety,
  Pets,
  Spa,
  LocalHospital,
  Category,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Store,
  Build,
  School,
  Instagram,
  Language,
  Link,
  Apps,
  AddCircleOutline,
  Phone,
  Psychology,
  FitnessCenter,
  ContentCut,
  Face,
  SelfImprovement,
  WbSunny,
  DarkMode,
  Translate,
} from "@mui/icons-material";
import { applyBodyTheme } from "../../context/GlobalThemeContext";
import { MuiTelInput } from "mui-tel-input";
import {
  ApiResponse,
  AuthResponse,
  completeRegistration,
} from "../../api/auth";
import {
  getIndustries,
  IndustryWithBusinessTypes,
  BusinessType,
} from "../../api/industries";
import { useRedux } from "../../hooks";
import { loginUser } from "../../redux/actions";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import Cookies from "js-cookie";
import logger from "../../utils/logger";
import logoJess from "../../assets/logos/Logo_jesss.svg";

// Module-level constants so Emotion doesn't re-hash these on every render
const BLOB_1_SX = {
  position: "absolute",
  top: "-8%",
  right: "-6%",
  width: { xs: "70vw", md: "42vw" },
  height: { xs: "70vw", md: "42vw" },
  maxWidth: 560,
  maxHeight: 560,
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(96,191,183,0.28) 0%, transparent 68%)",
  filter: "blur(48px)",
  zIndex: 0,
  pointerEvents: "none",
  animation: "aiPulse1 9s ease-in-out infinite",
  "@keyframes aiPulse1": {
    "0%, 100%": { transform: "translateY(0px) scale(1)", opacity: 0.9 },
    "50%": { transform: "translateY(-28px) scale(1.06)", opacity: 1 },
  },
} as const;

const BLOB_2_SX = {
  position: "absolute",
  bottom: "-6%",
  left: "-8%",
  width: { xs: "65vw", md: "38vw" },
  height: { xs: "65vw", md: "38vw" },
  maxWidth: 520,
  maxHeight: 520,
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(76,54,153,0.55) 0%, transparent 68%)",
  filter: "blur(52px)",
  zIndex: 0,
  pointerEvents: "none",
  animation: "aiPulse2 11s ease-in-out infinite",
  "@keyframes aiPulse2": {
    "0%, 100%": { transform: "translateY(0px) scale(1)", opacity: 0.85 },
    "50%": { transform: "translateY(24px) scale(1.09)", opacity: 1 },
  },
} as const;

const BLOB_3_SX = {
  position: "absolute",
  top: "28%",
  left: "5%",
  width: { xs: "40vw", md: "22vw" },
  height: { xs: "40vw", md: "22vw" },
  maxWidth: 300,
  maxHeight: 300,
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(147,240,144,0.14) 0%, transparent 70%)",
  filter: "blur(38px)",
  zIndex: 0,
  pointerEvents: "none",
  animation: "aiPulse3 13s ease-in-out infinite",
  "@keyframes aiPulse3": {
    "0%, 100%": { transform: "translateX(0px)", opacity: 0.7 },
    "50%": { transform: "translateX(18px)", opacity: 1 },
  },
} as const;

const BLOB_4_SX = {
  position: "absolute",
  top: "10%",
  left: "25%",
  width: { xs: "30vw", md: "18vw" },
  height: { xs: "30vw", md: "18vw" },
  maxWidth: 220,
  maxHeight: 220,
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(96,191,183,0.10) 0%, transparent 70%)",
  filter: "blur(30px)",
  zIndex: 0,
  pointerEvents: "none",
  animation: "aiPulse4 15s ease-in-out infinite",
  "@keyframes aiPulse4": {
    "0%, 100%": { transform: "scale(1)", opacity: 0.6 },
    "50%": { transform: "scale(1.15)", opacity: 0.9 },
  },
} as const;

const SCAN_LINE_SX = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "1px",
  background:
    "linear-gradient(90deg, transparent 0%, rgba(96,191,183,0.5) 30%, rgba(147,240,144,0.6) 50%, rgba(96,191,183,0.5) 70%, transparent 100%)",
  zIndex: 0,
  pointerEvents: "none",
  animation: "scanLine 6s linear infinite",
  "@keyframes scanLine": {
    "0%": { top: "-2px", opacity: 0 },
    "5%": { opacity: 1 },
    "95%": { opacity: 0.6 },
    "100%": { top: "100vh", opacity: 0 },
  },
} as const;

const getIndustryIcon = (code: string): React.ReactNode => {
  const iconProps = { sx: { fontSize: 40 } };
  const key = code.toLowerCase();

  if (key.includes("health")) {
    return <HealthAndSafety {...iconProps} />;
  }
  if (key.includes("beauty") || key.includes("aesthet")) {
    return <Spa {...iconProps} />;
  }
  if (key.includes("retail")) {
    return <Store {...iconProps} />;
  }
  if (key.includes("service")) {
    return <Build {...iconProps} />;
  }
  if (key.includes("educat")) {
    return <School {...iconProps} />;
  }

  return <Category {...iconProps} />;
};

const getBusinessTypeIcon = (code: string): React.ReactNode => {
  // Icon size will be controlled by parent Box component
  const key = code.toLowerCase();

  // ── Saúde ──────────────────────────────────────────────
  if (key.includes("vet")) {
    return <Pets />;
  }
  if (key.includes("dental")) {
    return <HealthAndSafety />;
  }
  if (key.includes("medical") || key.includes("clinic")) {
    return <LocalHospital />;
  }
  if (key.includes("psycholog") || key.includes("therapy")) {
    return <Psychology />;
  }
  if (key.includes("physiother") || key.includes("fisio")) {
    return <FitnessCenter />;
  }

  // ── Beleza & Estética ──────────────────────────────────
  if (key.includes("barber")) {
    return <ContentCut />;
  }
  if (key.includes("aesthet") || key.includes("estetic")) {
    return <Face />;
  }
  if (key.includes("spa") || key.includes("massage")) {
    return <SelfImprovement />;
  }
  if (key.includes("beauty") || key.includes("salon")) {
    return <Spa />;
  }

  // ── Outros ────────────────────────────────────────────
  if (key.includes("retail") || key.includes("store")) {
    return <Store />;
  }
  if (key.includes("service")) {
    return <Build />;
  }
  if (key.includes("consult")) {
    return <Business />;
  }
  if (key.includes("educat")) {
    return <School />;
  }

  return <Category />;
};

// Utility functions moved outside component to prevent re-creation on every render

// Helper function to extract Instagram ID from URL
const extractInstagramId = (input: string): string => {
  if (!input) return "";

  // Remove whitespace
  const trimmed = input.trim();

  // If it's just an ID (no URL structure), validate and return it
  if (!trimmed.includes("/") && !trimmed.includes("instagram.com")) {
    const username = trimmed.replace("@", "");
    // Instagram username validation: alphanumeric, underscores, and periods only
    const usernameRegex = /^[A-Za-z0-9_.]+$/;
    return usernameRegex.test(username) ? username : "";
  }

  // Extract from Instagram URL - must be instagram.com
  const instagramUrlRegex =
    /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([A-Za-z0-9_.]+)\/?(?:\?.*)?$/;
  const match = trimmed.match(instagramUrlRegex);

  if (match && match[1]) {
    return match[1];
  }

  return ""; // Return empty if not valid
};

// Helper function to validate Instagram input
const validateInstagram = (input: string): boolean => {
  if (!input) return true; // Empty is valid (optional)

  const id = extractInstagramId(input);
  // Valid if we could extract a valid ID
  return id !== "";
};

// Helper function to validate website URL
const validateWebsite = (url: string): boolean => {
  if (!url) return true; // Empty is valid (optional)

  const trimmed = url.trim();

  // Check if it has a domain-like structure (must have at least one dot)
  // Valid: example.com, www.example.com, sub.example.com
  // Invalid: example, localhost, single-word
  const domainRegex =
    /^(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/.*)?$/;

  if (!domainRegex.test(trimmed)) {
    return false;
  }

  try {
    // Add protocol if missing
    const urlToTest = trimmed.startsWith("http")
      ? trimmed
      : `https://${trimmed}`;
    const urlObj = new URL(urlToTest);

    // Additional validation: must have a proper domain
    return urlObj.hostname.includes(".");
  } catch {
    return false;
  }
};

// Helper function to format website URL
const formatWebsite = (url: string): string => {
  if (!url) return "";
  const trimmed = url.trim();
  if (!trimmed.startsWith("http")) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

// Helper function to get localized name based on current language
const getLocalizedName = (item: any): string => {
  const currentLang = i18n.language;

  // Map common language codes
  const langMap: { [key: string]: string } = {
    en: "en",
    pt: "pt",
    "pt-BR": "pt",
    es: "es",
    "es-ES": "es",
  };

  const langCode = langMap[currentLang] || "en";
  const nameField = `name_${langCode}`;

  // Try current language first, then fallback to other languages, then code
  if (item[nameField] && item[nameField].trim()) {
    return item[nameField];
  }

  // Fallback to other available languages
  const fallbackOrder = ["name_en", "name_pt", "name_es"];
  for (const field of fallbackOrder) {
    if (item[field] && item[field].trim()) {
      return item[field];
    }
  }

  // Final fallback to code or name
  return item.name || item.code || "Unknown";
};

const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dispatch } = useRedux();

  const [currentStep, setCurrentStep] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [industries, setIndustries] = useState<IndustryWithBusinessTypes[]>([]);
  const [selectedIndustry, setSelectedIndustry] =
    useState<IndustryWithBusinessTypes | null>(null);
  const [selectedBusinessType, setSelectedBusinessType] =
    useState<BusinessType | null>(null);
  const [instagramInput, setInstagramInput] = useState("");
  const [instagramId, setInstagramId] = useState("");
  const [website, setWebsite] = useState("");
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherAppInput, setOtherAppInput] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme") as "light" | "dark") || "light",
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    () => localStorage.getItem("language") || i18n.language || "pt",
  );

  // Fetch industries on component mount
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setIndustriesLoading(true);
        setError(null);
        const response = await getIndustries();
        setIndustries(response.data);
      } catch (err: any) {
        logger.error("Error fetching industries:", err);

        let errorMessage = t("onboarding.errorLoadingIndustries");

        if (err.code === "NETWORK_ERROR" || err.message === "Network Error") {
          errorMessage =
            "Connection error. Please check your internet connection and try again.";
        } else if (err.response?.status === 401) {
          errorMessage =
            "Authentication error. Please refresh the page and try again.";
        } else if (err.response?.status >= 500) {
          errorMessage = "Server error. Please try again in a few moments.";
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }

        setError(errorMessage);
      } finally {
        setIndustriesLoading(false);
      }
    };

    fetchIndustries();
  }, [t]);

  // Memoized Instagram ID to prevent unnecessary re-renders
  const instagramIdMemo = useMemo(() => {
    return extractInstagramId(instagramInput);
  }, [instagramInput]);

  // Update instagramId only when the memoized value changes
  useEffect(() => {
    setInstagramId(instagramIdMemo);
  }, [instagramIdMemo]);

  // Check if selected business type has apps
  const hasApps =
    selectedBusinessType &&
    selectedBusinessType.apps &&
    selectedBusinessType.apps.length > 0;
  const totalSteps = hasApps ? 8 : 7; // Last step is always preferences (theme + language)
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleThemeChange = useCallback((theme: "light" | "dark") => {
    setSelectedTheme(theme);
    localStorage.setItem("theme", theme);
    applyBodyTheme(theme === "dark");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", {
        detail: { isDark: theme === "dark" },
      }),
    );
  }, []);

  const handleLanguageChange = useCallback((lng: string) => {
    setSelectedLanguage(lng);
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.dispatchEvent(new Event("languageChanged"));
  }, []);

  const stepVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const cardVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
    tap: { scale: 0.98 },
  };

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      // Skip apps step if no apps available
      if (currentStep === 2 && !hasApps) {
        setCurrentStep(currentStep + 2); // Skip to Instagram step
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  }, [currentStep, totalSteps, hasApps]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      // Skip apps step if no apps available when going back
      if (currentStep === 4 && !hasApps) {
        setCurrentStep(currentStep - 2); // Skip back to business type step
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  }, [currentStep, hasApps]);

  const handleSubmit = async () => {
    // Get user ID from localStorage as userProfile might not have it
    const authUser = localStorage.getItem("authUser");
    const codeChallenge = Cookies.get("code_challenge");
    const port = Cookies.get("port");
    const redirectUri = Cookies.get("redirect_uri");
    const state = Cookies.get("state");

    let userId = null;

    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        userId =
          userData.id || userData.ID || userData.userId || userData.userID;
      } catch (e) {
        logger.error("Error parsing user data:", e);
      }
    }

    if (!userId || !companyName || !selectedBusinessType) {
      setError(t("onboarding.missingRequiredFields"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build preferences object
      const preferences: Record<string, string> = {};
      if (hasApps && selectedApps.size > 0) {
        let appIndex = 1;
        selectedApps.forEach((appId) => {
          if (appId === "other" && otherAppInput.trim()) {
            preferences["app_other"] = otherAppInput.trim();
          } else if (appId !== "other") {
            // Find the app name
            const app = selectedBusinessType.apps.find((a) => a.id === appId);
            if (app) {
              preferences[`app_${appIndex}`] = app.name;
              appIndex++;
            }
          }
        });
      }

      const { data: response } = await completeRegistration({
        userId: userId,
        companyName,
        businessType: selectedBusinessType.id,
        instagram: instagramId || "",
        website: website ? formatWebsite(website) : "",
        userPhone: formatPhoneNumber(phoneNumber),
        preferences:
          Object.keys(preferences).length > 0 ? preferences : undefined,
        codeChallenge: codeChallenge,
        redirectUri: redirectUri,
        state: state,
        port: port,
      });

      if (response.token) {
        dispatch(loginUser({ token: response.token }));
      }

      // Update localStorage with the new user data
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        try {
          const currentUserData = JSON.parse(authUser);
          const updatedUserData = {
            ...currentUserData,
            ...response,
            companyId: response.companyID,
            companyID: response.companyID,
          };
          localStorage.setItem("authUser", JSON.stringify(updatedUserData));
        } catch (e) {
          logger.error("Error updating user data:", e);
        }
      }

      // Redirect to chat-jess
      navigate("/chat-jess");
    } catch (err: any) {
      logger.error("Error completing registration:", err);

      let errorMessage = t("onboarding.submitError");

      if (err.code === "NETWORK_ERROR" || err.message === "Network Error") {
        errorMessage =
          "Connection error. Please check your internet connection and try again.";
      } else if (err.response?.status === 401) {
        errorMessage =
          "Authentication error. Please refresh the page and try again.";
      } else if (err.response?.status === 422) {
        errorMessage =
          "Invalid data provided. Please check your information and try again.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again in a few moments.";
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
    } finally {
      if (codeChallenge && redirectUri && state && port) {
        localStorage.removeItem("authUser");
        Cookies.remove("code_challenge");
        Cookies.remove("port");
        Cookies.remove("redirect_uri");
        Cookies.remove("state");

        navigate("/auth-login");
      }
      setLoading(false);
    }
  };

  const canProceed = useCallback(() => {
    // Adjust step numbers based on whether apps are available
    const actualStep = currentStep;

    switch (actualStep) {
      case 0:
        return companyName.trim().length >= 2;
      case 1:
        return selectedIndustry !== null;
      case 2:
        return selectedBusinessType !== null;
      case 3:
        // Apps step (only shown if hasApps)
        if (hasApps) {
          // Can proceed even without selection (optional step)
          return true;
        }
        // If no apps, this is Instagram step
        return !instagramInput || validateInstagram(instagramInput);
      case 4:
        // Instagram or Website step
        if (hasApps) {
          return !instagramInput || validateInstagram(instagramInput);
        }
        return !website || validateWebsite(website);
      case 5:
        // Website or Phone step
        if (hasApps) {
          return !website || validateWebsite(website);
        }
        // Phone step (required)
        return phoneNumber.trim().length > 0;
      case 6:
        // Phone step (hasApps) or preferences step (!hasApps)
        if (hasApps) {
          return phoneNumber.trim().length > 0;
        }
        return true; // preferences step — always can proceed
      case 7:
        // Preferences step (hasApps)
        return true;
      default:
        return false;
    }
  }, [
    currentStep,
    companyName,
    selectedIndustry,
    selectedBusinessType,
    hasApps,
    instagramInput,
    website,
    phoneNumber,
  ]);

  // Step names for the left panel navigation
  const stepNames = useMemo(() => {
    const names: string[] = [
      t("onboarding.welcomeTitle"),
      t("onboarding.selectIndustryTitle"),
      t("onboarding.selectBusinessTypeTitle"),
    ];
    if (hasApps) {
      names.push(t("onboarding.appsTitle"));
    }
    names.push(
      t("onboarding.instagramTitle"),
      t("onboarding.websiteTitle"),
      t("onboarding.phoneTitle"),
      t("onboarding.preferencesTitle"),
    );
    return names;
  }, [hasApps, t]);

  // Transparent wrapper — styling is handled by the right panel
  const StepWrapper: React.FC<{ children: React.ReactNode }> = useMemo(
    () =>
      ({ children }) => <Box sx={{ pb: 1 }}>{children}</Box>,
    [],
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <motion.div
            key="step-0"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Business sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.welcomeTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("onboarding.welcomeSubtitle")}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t("onboarding.companyNameLabel")}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                variant="outlined"
                size="medium"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontSize: "1.1rem",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    "&.Mui-focused": {
                      color: "#36276F",
                    },
                  },
                }}
                inputProps={{ maxLength: 100 }}
                helperText={t("onboarding.companyNameHelper")}
              />
            </StepWrapper>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            key="step-1"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.selectIndustryTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("onboarding.selectIndustrySubtitle")}
                </Typography>
              </Box>

              {error && !industriesLoading ? (
                <Box textAlign="center" py={4}>
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 3,
                      "& .MuiAlert-message": {
                        width: "100%",
                      },
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setError(null);
                        setIndustriesLoading(true);
                        const fetchIndustries = async () => {
                          try {
                            setError(null);
                            const response = await getIndustries();
                            setIndustries(response.data);
                          } catch (err: any) {
                            logger.error("Error fetching industries:", err);

                            let errorMessage = t(
                              "onboarding.errorLoadingIndustries",
                            );

                            if (
                              err.code === "NETWORK_ERROR" ||
                              err.message === "Network Error"
                            ) {
                              errorMessage =
                                "Connection error. Please check your internet connection and try again.";
                            } else if (err.response?.status === 401) {
                              errorMessage =
                                "Authentication error. Please refresh the page and try again.";
                            } else if (err.response?.status >= 500) {
                              errorMessage =
                                "Server error. Please try again in a few moments.";
                            } else if (err.response?.data?.error) {
                              errorMessage = err.response.data.error;
                            }

                            setError(errorMessage);
                          } finally {
                            setIndustriesLoading(false);
                          }
                        };
                        fetchIndustries();
                      }}
                      startIcon={<ArrowForward />}
                      sx={{ borderRadius: 2 }}
                    >
                      Try Again
                    </Button>
                  </Alert>
                </Box>
              ) : industriesLoading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3, 4].map((index) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={index} component="div">
                      <Skeleton
                        variant="rectangular"
                        height={180}
                        sx={{ borderRadius: 3 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  {industries.map((industry, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={industry.id}>
                      <motion.div
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        whileTap="tap"
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card
                          sx={{
                            cursor: "pointer",
                            border:
                              selectedIndustry?.id === industry.id
                                ? "2px solid"
                                : "1px solid",
                            borderColor:
                              selectedIndustry?.id === industry.id
                                ? "primary.main"
                                : "divider",
                            bgcolor:
                              selectedIndustry?.id === industry.id
                                ? "primary.50"
                                : "background.paper",
                            borderRadius: 3,
                            p: 2,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            setSelectedIndustry(industry);
                            setSelectedBusinessType(null); // Reset business type when industry changes
                          }}
                        >
                          <CardContent>
                            <Box color="primary.main" mb={2}>
                              {getIndustryIcon(industry.code)}
                            </Box>
                            <Typography variant="h6" fontWeight="bold">
                              {getLocalizedName(industry)}
                            </Typography>
                            {selectedIndustry?.id === industry.id && (
                              <CheckCircle
                                sx={{
                                  color: "primary.main",
                                  mt: 1,
                                  fontSize: 24,
                                }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              )}
            </StepWrapper>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="step-2"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.selectBusinessTypeTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("onboarding.selectBusinessTypeSubtitle")}
                </Typography>
              </Box>

              <Grid container spacing={3} justifyContent="center">
                {selectedIndustry?.business_types.map((businessType, index) => {
                  return (
                    <Grid
                      size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                      key={businessType.id}
                    >
                      <motion.div
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        whileTap="tap"
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card
                          sx={{
                            cursor: "pointer",
                            border:
                              selectedBusinessType?.id === businessType.id
                                ? "2px solid"
                                : "1px solid",
                            borderColor:
                              selectedBusinessType?.id === businessType.id
                                ? "primary.main"
                                : "divider",
                            bgcolor:
                              selectedBusinessType?.id === businessType.id
                                ? "primary.50"
                                : "background.paper",
                            borderRadius: 3,
                            p: 2,
                            height: "100%",
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: 3,
                              borderColor: "primary.light",
                            },
                          }}
                          onClick={() => setSelectedBusinessType(businessType)}
                        >
                          <Box
                            color="primary.main"
                            mb={1.5}
                            sx={{
                              "& svg": {
                                fontSize: 40,
                              },
                            }}
                          >
                            {getBusinessTypeIcon(businessType.code)}
                          </Box>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              fontSize: "0.9rem",
                              lineHeight: 1.2,
                              color: "text.primary",
                              mb:
                                selectedBusinessType?.id === businessType.id
                                  ? 1
                                  : 0,
                              wordBreak: "break-word",
                              hyphens: "auto",
                            }}
                          >
                            {getLocalizedName(businessType)}
                          </Typography>
                          {selectedBusinessType?.id === businessType.id && (
                            <CheckCircle
                              sx={{
                                color: "primary.main",
                                mt: 0.5,
                                fontSize: 20,
                              }}
                            />
                          )}
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </StepWrapper>
          </motion.div>
        );

      case 3:
        // If business type has apps, show apps selection
        if (hasApps) {
          return (
            <motion.div
              key="step-3-apps"
              custom={1}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <StepWrapper>
                <Box textAlign="center" mb={4}>
                  <Apps sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    gutterBottom
                    color="#36276F"
                  >
                    {t("onboarding.appsTitle")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" mb={1}>
                    {t("onboarding.appsSubtitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {t("onboarding.optional")}
                  </Typography>
                </Box>

                <Box sx={{ maxWidth: 1000, mx: "auto" }}>
                  <Grid container spacing={2} justifyContent="center">
                    {selectedBusinessType.apps.map((app) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={app.id}>
                        <motion.div
                          variants={cardVariants}
                          initial="initial"
                          animate="animate"
                          whileHover="hover"
                          whileTap="tap"
                        >
                          <Card
                            sx={{
                              cursor: "pointer",
                              height: 160,
                              minHeight: 160,
                              maxHeight: 160,
                              maxWidth: 280,
                              mx: "auto",
                              position: "relative",
                              border: selectedApps.has(app.id)
                                ? "2px solid"
                                : "1px solid",
                              borderColor: selectedApps.has(app.id)
                                ? "primary.main"
                                : "divider",
                              bgcolor: selectedApps.has(app.id)
                                ? "primary.50"
                                : "background.paper",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: "primary.light",
                                boxShadow: 2,
                              },
                            }}
                            onClick={() => {
                              const newSelectedApps = new Set(selectedApps);
                              if (newSelectedApps.has(app.id)) {
                                newSelectedApps.delete(app.id);
                              } else {
                                newSelectedApps.add(app.id);
                              }
                              setSelectedApps(newSelectedApps);
                            }}
                          >
                            {selectedApps.has(app.id) && (
                              <CheckCircle
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  color: "primary.main",
                                  fontSize: 20,
                                  zIndex: 1,
                                }}
                              />
                            )}
                            <CardContent
                              sx={{
                                textAlign: "center",
                                p: 2,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {app.icon_url && (
                                <Box
                                  component="img"
                                  src={app.icon_url}
                                  alt={app.name}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    mb: 1,
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                              <Typography
                                variant="body1"
                                fontWeight="600"
                                sx={{ mb: 0.5 }}
                              >
                                {app.name}
                              </Typography>
                              {app.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  title={app.description}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.2,
                                    height: "2.4em",
                                    cursor: "help",
                                  }}
                                >
                                  {app.description}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}

                    {/* Other option */}
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <motion.div
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <Card
                          sx={{
                            cursor: "pointer",
                            height: 160,
                            minHeight: 160,
                            maxHeight: 160,
                            maxWidth: 280,
                            mx: "auto",
                            position: "relative",
                            border: selectedApps.has("other")
                              ? "2px solid"
                              : "1px solid",
                            borderColor: selectedApps.has("other")
                              ? "primary.main"
                              : "divider",
                            bgcolor: selectedApps.has("other")
                              ? "primary.50"
                              : "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.light",
                              boxShadow: 2,
                            },
                          }}
                          onClick={() => {
                            const newSelectedApps = new Set(selectedApps);
                            if (newSelectedApps.has("other")) {
                              newSelectedApps.delete("other");
                              setShowOtherInput(false);
                              setOtherAppInput("");
                            } else {
                              newSelectedApps.add("other");
                              setShowOtherInput(true);
                            }
                            setSelectedApps(newSelectedApps);
                          }}
                        >
                          {selectedApps.has("other") && (
                            <CheckCircle
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "primary.main",
                                fontSize: 20,
                                zIndex: 1,
                              }}
                            />
                          )}
                          <CardContent
                            sx={{
                              textAlign: "center",
                              p: 2,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <AddCircleOutline
                              sx={{
                                fontSize: 48,
                                color: "primary.main",
                                mb: 1,
                              }}
                            />
                            <Typography
                              variant="body1"
                              fontWeight="600"
                              sx={{ mb: 0.5 }}
                            >
                              {t("onboarding.otherApp")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.2,
                                height: "2.4em",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {t("onboarding.otherAppDescription")}
                            </Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Box>

                {showOtherInput && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TextField
                      fullWidth
                      label={t("onboarding.otherAppLabel")}
                      value={otherAppInput}
                      onChange={(e) => setOtherAppInput(e.target.value)}
                      variant="outlined"
                      size="medium"
                      sx={{
                        mt: 3,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                      placeholder={t("onboarding.otherAppPlaceholder")}
                      helperText={t("onboarding.otherAppHelper")}
                    />
                  </motion.div>
                )}
              </StepWrapper>
            </motion.div>
          );
        }

        // Otherwise show Instagram step
        return (
          <motion.div
            key="step-3"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Instagram sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.instagramTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={1}>
                  {t("onboarding.instagramSubtitle")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {t("onboarding.optional")}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t("onboarding.instagramLabel")}
                value={instagramInput}
                onChange={(e) => setInstagramInput(e.target.value)}
                variant="outlined"
                size="medium"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontSize: "1.1rem",
                  },
                }}
                placeholder={t("onboarding.instagramPlaceholder")}
                helperText={
                  instagramInput && !validateInstagram(instagramInput)
                    ? t("onboarding.instagramError")
                    : t("onboarding.instagramHelper")
                }
                error={Boolean(
                  instagramInput && !validateInstagram(instagramInput),
                )}
              />

              {instagramId && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    component="a"
                    href={`https://instagram.com/${instagramId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      bgcolor: "success.50",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "success.200",
                      textDecoration: "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "success.100",
                        borderColor: "success.300",
                        transform: "translateY(-1px)",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Instagram sx={{ color: "success.main", mr: 1 }} />
                    <Typography
                      variant="body1"
                      color="success.main"
                      fontWeight="medium"
                    >
                      @{instagramId}
                    </Typography>
                    <Box sx={{ ml: 1, opacity: 0.7 }}>
                      <Link sx={{ fontSize: 16, color: "success.main" }} />
                    </Box>
                  </Box>
                </motion.div>
              )}
            </StepWrapper>
          </motion.div>
        );

      case 4:
        // If business type has apps, show Instagram step
        if (hasApps) {
          return (
            <motion.div
              key="step-4-instagram"
              custom={1}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <StepWrapper>
                <Box textAlign="center" mb={4}>
                  <Instagram sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    gutterBottom
                    color="#36276F"
                  >
                    {t("onboarding.instagramTitle")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" mb={1}>
                    {t("onboarding.instagramSubtitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {t("onboarding.optional")}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label={t("onboarding.instagramLabel")}
                  value={instagramInput}
                  onChange={(e) => setInstagramInput(e.target.value)}
                  variant="outlined"
                  size="medium"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: "1.1rem",
                    },
                  }}
                  placeholder={t("onboarding.instagramPlaceholder")}
                  helperText={
                    instagramInput && !validateInstagram(instagramInput)
                      ? t("onboarding.instagramError")
                      : t("onboarding.instagramHelper")
                  }
                  error={Boolean(
                    instagramInput && !validateInstagram(instagramInput),
                  )}
                />

                {instagramId && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      component="a"
                      href={`https://instagram.com/${instagramId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        bgcolor: "success.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "success.200",
                        textDecoration: "none",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "success.100",
                          borderColor: "success.300",
                          transform: "translateY(-1px)",
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Instagram sx={{ color: "success.main", mr: 1 }} />
                      <Typography
                        variant="body1"
                        color="success.main"
                        fontWeight="medium"
                      >
                        @{instagramId}
                      </Typography>
                      <Box sx={{ ml: 1, opacity: 0.7 }}>
                        <Link sx={{ fontSize: 16, color: "success.main" }} />
                      </Box>
                    </Box>
                  </motion.div>
                )}
              </StepWrapper>
            </motion.div>
          );
        }

        // Otherwise show website step
        return (
          <motion.div
            key="step-4"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Language sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.websiteTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={1}>
                  {t("onboarding.websiteSubtitle")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {t("onboarding.optional")}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t("onboarding.websiteLabel")}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                variant="outlined"
                size="medium"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontSize: "1.1rem",
                  },
                }}
                placeholder={t("onboarding.websitePlaceholder")}
                helperText={
                  website && !validateWebsite(website)
                    ? t("onboarding.websiteError")
                    : t("onboarding.websiteHelper")
                }
                error={Boolean(website && !validateWebsite(website))}
              />

              {website && validateWebsite(website) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    component="a"
                    href={formatWebsite(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      bgcolor: "success.50",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "success.200",
                      textDecoration: "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "success.100",
                        borderColor: "success.300",
                        transform: "translateY(-1px)",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Language sx={{ color: "success.main", mr: 1 }} />
                    <Typography
                      variant="body1"
                      color="success.main"
                      fontWeight="medium"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "300px",
                      }}
                    >
                      {formatWebsite(website)}
                    </Typography>
                    <Box sx={{ ml: 1, opacity: 0.7 }}>
                      <Link sx={{ fontSize: 16, color: "success.main" }} />
                    </Box>
                  </Box>
                </motion.div>
              )}
            </StepWrapper>
          </motion.div>
        );

      case 5:
        // Only shown if business type has apps - this is the website step
        if (hasApps) {
          return (
            <motion.div
              key="step-5-website"
              custom={1}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <StepWrapper>
                <Box textAlign="center" mb={4}>
                  <Language sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    gutterBottom
                    color="#36276F"
                  >
                    {t("onboarding.websiteTitle")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" mb={1}>
                    {t("onboarding.websiteSubtitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {t("onboarding.optional")}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label={t("onboarding.websiteLabel")}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  variant="outlined"
                  size="medium"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: "1.1rem",
                    },
                  }}
                  placeholder={t("onboarding.websitePlaceholder")}
                  helperText={
                    website && !validateWebsite(website)
                      ? t("onboarding.websiteError")
                      : t("onboarding.websiteHelper")
                  }
                  error={Boolean(website && !validateWebsite(website))}
                />

                {website && validateWebsite(website) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      component="a"
                      href={formatWebsite(website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        bgcolor: "success.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "success.200",
                        textDecoration: "none",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "success.100",
                          borderColor: "success.300",
                          transform: "translateY(-1px)",
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Language sx={{ color: "success.main", mr: 1 }} />
                      <Typography
                        variant="body1"
                        color="success.main"
                        fontWeight="medium"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "300px",
                        }}
                      >
                        {formatWebsite(website)}
                      </Typography>
                      <Box sx={{ ml: 1, opacity: 0.7 }}>
                        <Link sx={{ fontSize: 16, color: "success.main" }} />
                      </Box>
                    </Box>
                  </motion.div>
                )}
              </StepWrapper>
            </motion.div>
          );
        }
        // Otherwise show phone step
        return (
          <motion.div
            key="step-5"
            custom={1}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <StepWrapper>
              <Box textAlign="center" mb={4}>
                <Phone sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color="#36276F"
                >
                  {t("onboarding.phoneTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("onboarding.phoneSubtitle")}
                </Typography>
              </Box>

              <Box sx={{ maxWidth: 500, mx: "auto" }}>
                <MuiTelInput
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                  defaultCountry="BR"
                  preferredCountries={["BR", "US", "GB", "PT", "ES"]}
                  fullWidth
                  size="medium"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: "1.1rem",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("onboarding.phoneHelper")}
                </Typography>
              </Box>
            </StepWrapper>
          </motion.div>
        );

      case 6:
        // Phone step (only if hasApps)
        if (hasApps) {
          return (
            <motion.div
              key="step-6-phone"
              custom={1}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <StepWrapper>
                <Box textAlign="center" mb={4}>
                  <Phone sx={{ fontSize: 52, color: "#36276F", mb: 1.5 }} />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    gutterBottom
                    color="#36276F"
                  >
                    {t("onboarding.phoneTitle")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t("onboarding.phoneSubtitle")}
                  </Typography>
                </Box>

                <Box sx={{ maxWidth: 500, mx: "auto" }}>
                  <MuiTelInput
                    value={phoneNumber}
                    onChange={(value) => setPhoneNumber(value)}
                    defaultCountry="BR"
                    preferredCountries={["BR", "US", "GB", "PT", "ES"]}
                    fullWidth
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        fontSize: "1.1rem",
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {t("onboarding.phoneHelper")}
                  </Typography>
                </Box>
              </StepWrapper>
            </motion.div>
          );
        }
        // !hasApps → preferences step
        return renderPreferencesStep("step-6-preferences");

      case 7:
        // hasApps → preferences step
        if (hasApps) {
          return renderPreferencesStep("step-7-preferences");
        }
        return null;

      default:
        return null;
    }
  };

  const LANGUAGES = [
    { code: "pt", name: "Português", flag: "🇧🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
  ];

  const renderPreferencesStep = (key: string) => (
    <motion.div
      key={key}
      custom={1}
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <StepWrapper>
        {/* Header */}
        <Box textAlign="center" mb={5}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #36276F 0%, #60BFB7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: "0 8px 24px rgba(54,39,111,0.25)",
            }}
          >
            <Translate sx={{ fontSize: 32, color: "#fff" }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            color="#36276F"
          >
            {t("onboarding.preferencesTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("onboarding.preferencesSubtitle")}
          </Typography>
        </Box>

        {/* Theme selection */}
        <Box mb={4}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#36276F"
            mb={2}
          >
            {t("onboarding.themeLabel")}
          </Typography>
          <Box display="flex" gap={2}>
            {(["light", "dark"] as const).map((themeOpt) => {
              const isSelected = selectedTheme === themeOpt;
              const isDarkOpt = themeOpt === "dark";
              return (
                <Box
                  key={themeOpt}
                  onClick={() => handleThemeChange(themeOpt)}
                  sx={{
                    flex: 1,
                    cursor: "pointer",
                    borderRadius: "16px",
                    border: isSelected
                      ? "2.5px solid #36276F"
                      : "1.5px solid rgba(54,39,111,0.15)",
                    overflow: "hidden",
                    transition: "all 0.25s ease",
                    boxShadow: isSelected
                      ? "0 0 0 4px rgba(54,39,111,0.12), 0 4px 16px rgba(54,39,111,0.18)"
                      : "0 2px 8px rgba(0,0,0,0.06)",
                    "&:hover": {
                      borderColor: "#36276F",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(54,39,111,0.15)",
                    },
                  }}
                >
                  {/* Preview area */}
                  <Box
                    sx={{
                      height: 80,
                      background: isDarkOpt
                        ? "linear-gradient(135deg, #141414 0%, #1e1e1e 100%)"
                        : "linear-gradient(135deg, #f7f8fa 0%, #eeeef0 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Mini UI preview */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 12,
                        right: 12,
                      }}
                    >
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          background: isDarkOpt
                            ? "rgba(96,191,183,0.5)"
                            : "rgba(54,39,111,0.3)",
                          mb: 0.75,
                          width: "60%",
                        }}
                      />
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 4,
                          background: isDarkOpt
                            ? "rgba(255,255,255,0.15)"
                            : "rgba(0,0,0,0.1)",
                          width: "80%",
                          mb: 0.5,
                        }}
                      />
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 4,
                          background: isDarkOpt
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.07)",
                          width: "50%",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: isDarkOpt
                          ? "linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%)"
                          : "linear-gradient(135deg, #fff 0%, #f0f0f5 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isDarkOpt
                          ? "0 0 20px rgba(96,191,183,0.3)"
                          : "0 4px 12px rgba(54,39,111,0.15)",
                        border: isDarkOpt
                          ? "1px solid rgba(96,191,183,0.3)"
                          : "1px solid rgba(54,39,111,0.1)",
                      }}
                    >
                      {isDarkOpt ? (
                        <DarkMode sx={{ fontSize: 18, color: "#93F090" }} />
                      ) : (
                        <WbSunny sx={{ fontSize: 18, color: "#36276F" }} />
                      )}
                    </Box>
                  </Box>

                  {/* Label area */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: isSelected ? "rgba(54,39,111,0.04)" : "#fff",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={isSelected ? 700 : 500}
                      color={isSelected ? "#36276F" : "text.primary"}
                    >
                      {isDarkOpt
                        ? t("onboarding.themeDark")
                        : t("onboarding.themeLight")}
                    </Typography>
                    {isSelected && (
                      <CheckCircle sx={{ fontSize: 18, color: "#36276F" }} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Language selection */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#36276F"
            mb={2}
          >
            {t("onboarding.languageLabel")}
          </Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {LANGUAGES.map((lang) => {
              const isSelected =
                selectedLanguage === lang.code || i18n.language === lang.code;
              return (
                <Box
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  sx={{
                    flex: "1 1 calc(33% - 8px)",
                    minWidth: 110,
                    cursor: "pointer",
                    borderRadius: "12px",
                    border: isSelected
                      ? "2.5px solid #36276F"
                      : "1.5px solid rgba(54,39,111,0.15)",
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    transition: "all 0.2s ease",
                    background: isSelected ? "rgba(54,39,111,0.05)" : "#fff",
                    boxShadow: isSelected
                      ? "0 0 0 3px rgba(54,39,111,0.1)"
                      : "0 1px 4px rgba(0,0,0,0.05)",
                    "&:hover": {
                      borderColor: "#36276F",
                      background: "rgba(54,39,111,0.04)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Typography fontSize="1.4rem" lineHeight={1}>
                    {lang.flag}
                  </Typography>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={isSelected ? 700 : 500}
                      color={isSelected ? "#36276F" : "text.primary"}
                    >
                      {lang.name}
                    </Typography>
                  </Box>
                  {isSelected && (
                    <CheckCircle
                      sx={{ fontSize: 16, color: "#36276F", ml: "auto" }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </StepWrapper>
    </motion.div>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // Deep digital space — base para a funcionária digital
        background:
          "linear-gradient(145deg, #08061A 0%, #0E1635 40%, #091E1E 100%)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, sm: 2, md: 3 },
      }}
    >
      {/* ── BACKGROUND LAYER: dot-grid (dados / neurônios em repouso) ── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(147, 240, 144, 0.10) 1px, transparent 1px)",
          backgroundSize: "38px 38px",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* ── BLOB 1 — pulso teal (top-right) ── */}
      <Box sx={BLOB_1_SX} />

      {/* ── BLOB 2 — pulso roxo (bottom-left) ── */}
      <Box sx={BLOB_2_SX} />

      {/* ── BLOB 3 — verde vivo (centro-esquerdo) ── */}
      <Box sx={BLOB_3_SX} />

      {/* ── BLOB 4 — teal secundário (top-left faint) ── */}
      <Box sx={BLOB_4_SX} />

      {/* ── LINHA DE SCAN (IA "lendo" o ambiente) ── */}
      <Box sx={SCAN_LINE_SX} />

      {/* ── GRADIENTE VINHETA (foca o centro, mergulha bordas) ── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 40%, rgba(8,6,26,0.65) 100%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 980,
          minHeight: { xs: "100vh", sm: "auto" },
          borderRadius: { xs: 0, sm: "20px" },
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          boxShadow: {
            xs: "none",
            sm: "0 0 0 1px rgba(96,191,183,0.12), 0 32px 80px rgba(8,6,26,0.6), 0 8px 32px rgba(54,39,111,0.3)",
          },
        }}
      >
        {/* ── LEFT PANEL ── */}
        <Box
          sx={{
            width: { xs: "100%", md: 268 },
            background: "linear-gradient(180deg, #93F090 0%, #60BFB7 100%)",
            display: "flex",
            flexDirection: "column",
            p: { xs: "1.5rem 2rem", md: "2.5rem 2rem" },
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              bottom: -70,
              right: -70,
              width: 240,
              height: 240,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.07)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: -40,
              left: -40,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "40%",
              right: -20,
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />

          {/* Logo */}
          <Box
            component="img"
            src={logoJess}
            alt="Jesss"
            sx={{
              width: 44,
              height: 44,
              objectFit: "contain",
              mb: { xs: 0, md: "2.5rem" },
            }}
          />

          {/* Steps list — visible on desktop */}
          <Box
            sx={{
              flex: 1,
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {stepNames.map((name, idx) => {
              const isDone = idx < currentStep;
              const isActive = idx === currentStep;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 0.75,
                    px: 1,
                    borderRadius: 2,
                    background: isActive
                      ? "rgba(255,255,255,0.15)"
                      : "transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Circle indicator */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isDone
                        ? "rgba(255,255,255,0.9)"
                        : isActive
                          ? "#FFFFFF"
                          : "rgba(255,255,255,0.22)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: isActive
                        ? "0 2px 10px rgba(0,0,0,0.18)"
                        : "none",
                      transition: "all 0.35s ease",
                    }}
                  >
                    {isDone ? (
                      <CheckCircle sx={{ fontSize: 15, color: "#36276F" }} />
                    ) : (
                      <Typography
                        sx={{
                          color: isActive ? "#36276F" : "rgba(54,39,111,0.45)",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          lineHeight: 1,
                        }}
                      >
                        {idx + 1}
                      </Typography>
                    )}
                  </Box>

                  {/* Step name */}
                  <Typography
                    sx={{
                      color: isActive
                        ? "#FFFFFF"
                        : isDone
                          ? "rgba(255,255,255,0.85)"
                          : "rgba(255,255,255,0.5)",
                      fontWeight: isActive ? 600 : 400,
                      fontSize: "0.84rem",
                      lineHeight: 1.3,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Progress dots — mobile */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 0.75,
              alignItems: "center",
              mt: 1.5,
            }}
          >
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: idx === currentStep ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  background:
                    idx <= currentStep ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                  transition: "all 0.35s ease",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* ── RIGHT PANEL ── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "#FFFFFF",
            minHeight: { md: 640 },
            overflow: "hidden",
          }}
        >
          {/* Error banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Alert
                severity="error"
                sx={{
                  borderRadius: 0,
                  border: "none",
                  borderBottom: "1px solid rgba(211, 47, 47, 0.15)",
                }}
              >
                {error}
              </Alert>
            </motion.div>
          )}

          {/* Step content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: { xs: "2rem 1.5rem", md: "2.5rem 3rem" },
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* ── Navigation footer ── */}
          <Box
            sx={{
              px: { xs: "1.5rem", md: "3rem" },
              py: "1.25rem",
              borderTop: "1px solid rgba(54, 39, 111, 0.07)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#FAFBFF",
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleBack}
              disabled={currentStep === 0 || loading}
              startIcon={<ArrowBack />}
              variant="outlined"
              size="medium"
              sx={{
                borderRadius: 3,
                borderColor: "rgba(54, 39, 111, 0.22)",
                color: "#36276F",
                bgcolor: "transparent",
                px: 2.5,
                py: 1.1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.9rem",
                "&:hover": {
                  borderColor: "#36276F",
                  bgcolor: "rgba(54, 39, 111, 0.04)",
                },
                "&:disabled": {
                  opacity: 0.28,
                  borderColor: "rgba(54, 39, 111, 0.15)",
                  color: "rgba(54, 39, 111, 0.35)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {t("onboarding.back")}
            </Button>

            <Box display="flex" gap={1.5} alignItems="center">
              {/* Skip button for optional steps */}
              {((currentStep === 3 && hasApps) ||
                (currentStep === 4 && hasApps) ||
                (currentStep === 3 && !hasApps) ||
                (currentStep === 4 && !hasApps) ||
                (currentStep === 5 && hasApps)) && (
                <Button
                  onClick={handleNext}
                  variant="text"
                  size="medium"
                  sx={{
                    borderRadius: 3,
                    color: "rgba(54, 39, 111, 0.4)",
                    px: 2.5,
                    py: 1.1,
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: "0.9rem",
                    "&:hover": {
                      bgcolor: "rgba(54, 39, 111, 0.04)",
                      color: "#36276F",
                    },
                  }}
                >
                  {t("onboarding.skip")}
                </Button>
              )}

              {currentStep === totalSteps - 1 ? (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || loading}
                  variant="contained"
                  size="medium"
                  sx={{
                    borderRadius: 3,
                    px: 3.5,
                    py: 1.1,
                    background:
                      "linear-gradient(135deg, #93F090 0%, #60BFB7 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    boxShadow: "0 4px 18px rgba(96, 191, 183, 0.32)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #7dd87a 0%, #4da8a1 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 24px rgba(96, 191, 183, 0.48)",
                    },
                    "&:disabled": {
                      background: "rgba(54, 39, 111, 0.08)",
                      color: "rgba(54, 39, 111, 0.3)",
                      boxShadow: "none",
                    },
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    t("onboarding.finish")
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  endIcon={<ArrowForward />}
                  variant="contained"
                  size="medium"
                  sx={{
                    borderRadius: 3,
                    px: 3.5,
                    py: 1.1,
                    background:
                      "linear-gradient(135deg, #93F090 0%, #60BFB7 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    boxShadow: "0 4px 18px rgba(96, 191, 183, 0.32)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #7dd87a 0%, #4da8a1 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 24px rgba(96, 191, 183, 0.48)",
                    },
                    "&:disabled": {
                      background: "rgba(54, 39, 111, 0.08)",
                      color: "rgba(54, 39, 111, 0.3)",
                      boxShadow: "none",
                    },
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {t("onboarding.next")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default OnboardingWizard;
